import * as z from "zod";

export const ValidateTag = z
    .string()
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9-]+:[a-zA-Z0-9$-]+$/, "Invalid tag.");

export type ValidateTag = z.infer<typeof ValidateTag>;

export const TagCategory = z.enum([
    "activities",
    "cuisine",
    "goodFor",
    "meals",
    "price",
    "type",
    "vibe",
]);
export type TagCategory = z.infer<typeof TagCategory>;

export const Tag = z.enum(
    [
        "price:free",
        "price:inexpensive",
        "price:moderate",
        "price:expensive",
        "price:very-expensive",

        "type:restaurant",
        "type:cafe",
        "type:pop-up",
        "type:bar",
        "type:pub",
        "type:wine-bar",
        "type:cocktail-bar",
        "type:rooftop",
        "type:nightclub",
        "type:live-music",
        "type:commercial-airline",
        "type:private-airline",
        "type:hotel",
        "type:villa",
        "type:apartment",
        "type:house",
        "type:resort",
        "type:yacht",
        "type:bed-and-breakfast",
        "type:car-rental",
        "type:car-transfer",
        "type:boat-rental",
        "type:charter-boat",
        "type:helicopter",
        "type:water-taxi",
        "type:sports",
        "type:theatre",
        "type:music",
        "type:fashion",
        "type:art",
        "type:dance",
        "type:museum",
        "type:art-gallery",
        "type:historical",
        "type:tourist-attraction",
        "type:must-do",
        "type:hidden-gem",
        "type:outdoors",
        "type:bookshop",
        "type:watches",
        "type:corporate-gifts",
        "type:personal-gifts",
        "type:collectibles",
        "type:spa",
        "type:sauna",
        "type:yoga",
        "type:ayurveda",
        "type:reiki",
        "type:massage",
        "type:beauty-salon",
        "type:personal-trainer",
        "type:retreat",
        "type:surfing",
        "type:wellness-hotel",
        "type:workout-studio",
        "type:flower-delivery",
        "type:cleaning-service",
        "type:moving-service",
        "type:real-estate",
        "type:education",
        "type:childcare",
        "type:bakery",
        "type:food-court",

        "vibe:boutique",
        "vibe:casual",
        "vibe:elegant",
        "vibe:high-end",
        "vibe:local-and-authentic",
        "vibe:luxury",
        "vibe:modern",
        "vibe:quirky",
        "vibe:romantic",
        "vibe:touristy",
        "vibe:traditional",
        "vibe:trendy",
        "vibe:fine-dining",
        "vibe:intimate",

        "goodFor:business",
        "goodFor:couples",
        "goodFor:dates",
        "goodFor:families",
        "goodFor:foodies",
        "goodFor:groups",
        "goodFor:locals",
        "goodFor:parties",
        "goodFor:private-dining",
        "goodFor:tourists",
        "goodFor:vegans",
        "goodFor:vegetarians",
        "goodFor:friends",
        "goodFor:friends-catchup",
        "goodFor:solo-trip",

        "meals:breakfast",
        "meals:brunch",
        "meals:lunch",
        "meals:dinner",
        "meals:quick-bites",

        "cuisine:african",
        "cuisine:american",
        "cuisine:asian",
        "cuisine:british",
        "cuisine:chinese",
        "cuisine:french",
        "cuisine:greek",
        "cuisine:indian",
        "cuisine:italian",
        "cuisine:japanese",
        "cuisine:latin",
        "cuisine:mediterranean",
        "cuisine:mexican",
        "cuisine:middle-eastern",
        "cuisine:scandinavian",
        "cuisine:seafood",
        "cuisine:spanish",
        "cuisine:steakhouse",

        "activities:adventurous",
        "activities:beach",
        "activities:city-break",
        "activities:cultural",
        "activities:hiking",
        "activities:nature",
        "activities:sea",
        "activities:safari",
        "activities:skiing",
    ],
    { error: (iss) => `Invalid tag: received "${iss.input}"` },
);
export type Tag = z.infer<typeof Tag>;

export type TagMap = Record<string, Tag>;
export type AllTags = Record<TagCategory, TagMap>;
export type CategorySpecificTags = Partial<Record<TagCategory, TagMap>>;

export const Tags: AllTags = {
    price: {
        veryExpensive: Tag.enum["price:very-expensive"],
        expensive: Tag.enum["price:expensive"],
        moderate: Tag.enum["price:moderate"],
        inexpensive: Tag.enum["price:inexpensive"],
        free: Tag.enum["price:free"],
    },
    type: {
        restaurant: Tag.enum["type:restaurant"],
        cafe: Tag.enum["type:cafe"],
        popUp: Tag.enum["type:pop-up"],

        bar: Tag.enum["type:bar"],
        pub: Tag.enum["type:pub"],
        wineBar: Tag.enum["type:wine-bar"],
        cocktailBar: Tag.enum["type:cocktail-bar"],
        rooftop: Tag.enum["type:rooftop"],
        nightclub: Tag.enum["type:nightclub"],
        liveMusic: Tag.enum["type:live-music"],
        commercialAirline: Tag.enum["type:commercial-airline"],
        privateAirline: Tag.enum["type:private-airline"],
        hotel: Tag.enum["type:hotel"],
        villa: Tag.enum["type:villa"],
        apartment: Tag.enum["type:apartment"],
        house: Tag.enum["type:house"],
        resort: Tag.enum["type:resort"],
        yacht: Tag.enum["type:yacht"],
        bedAndBreakfast: Tag.enum["type:bed-and-breakfast"],
        carRental: Tag.enum["type:car-rental"],
        carTransfer: Tag.enum["type:car-transfer"],
        boatRental: Tag.enum["type:boat-rental"],
        charterBoat: Tag.enum["type:charter-boat"],
        helicopter: Tag.enum["type:helicopter"],
        waterTaxi: Tag.enum["type:water-taxi"],
        sports: Tag.enum["type:sports"],
        theatre: Tag.enum["type:theatre"],
        music: Tag.enum["type:music"],
        fashion: Tag.enum["type:fashion"],
        art: Tag.enum["type:art"],
        dance: Tag.enum["type:dance"],
        museum: Tag.enum["type:museum"],
        artGallery: Tag.enum["type:art-gallery"],
        historical: Tag.enum["type:historical"],
        touristAttraction: Tag.enum["type:tourist-attraction"],
        mustDo: "type:must-do",
        hiddenGem: "type:hidden-gem",
        outdoors: Tag.enum["type:outdoors"],
        bookshop: Tag.enum["type:bookshop"],
        watches: Tag.enum["type:watches"],
        corporateGifts: Tag.enum["type:corporate-gifts"],
        personalGifts: Tag.enum["type:personal-gifts"],
        collectibles: Tag.enum["type:collectibles"],
        spa: Tag.enum["type:spa"],
        sauna: Tag.enum["type:sauna"],
        yoga: Tag.enum["type:yoga"],
        ayurveda: Tag.enum["type:ayurveda"],
        reiki: Tag.enum["type:reiki"],
        massage: Tag.enum["type:massage"],
        beautySalon: Tag.enum["type:beauty-salon"],
        personalTrainer: Tag.enum["type:personal-trainer"],
        retreat: Tag.enum["type:retreat"],
        surfing: Tag.enum["type:surfing"],
        wellnessHotel: Tag.enum["type:wellness-hotel"],
        workoutStudio: Tag.enum["type:workout-studio"],
        flowerDelivery: Tag.enum["type:flower-delivery"],
        cleaningService: Tag.enum["type:cleaning-service"],
        movingService: Tag.enum["type:moving-service"],
        realEstate: Tag.enum["type:real-estate"],
        education: Tag.enum["type:education"],
        childcare: Tag.enum["type:childcare"],
        bakery: Tag.enum["type:bakery"],
        foodCourt: Tag.enum["type:food-court"],
    },
    meals: {
        breakfast: Tag.enum["meals:breakfast"],
        brunch: Tag.enum["meals:brunch"],
        dinner: Tag.enum["meals:dinner"],
        lunch: Tag.enum["meals:lunch"],
        quickBites: Tag.enum["meals:quick-bites"],
    },
    vibe: {
        boutique: Tag.enum["vibe:boutique"],
        casual: Tag.enum["vibe:casual"],
        elegant: Tag.enum["vibe:elegant"],
        fineDining: Tag.enum["vibe:fine-dining"],
        highEnd: Tag.enum["vibe:high-end"],
        intimate: Tag.enum["vibe:intimate"],
        localAndAuthentic: Tag.enum["vibe:local-and-authentic"],
        luxury: Tag.enum["vibe:luxury"],
        modern: Tag.enum["vibe:modern"],
        quirky: Tag.enum["vibe:quirky"],
        romantic: Tag.enum["vibe:romantic"],
        touristy: Tag.enum["vibe:touristy"],
        traditional: Tag.enum["vibe:traditional"],
        trendy: Tag.enum["vibe:trendy"],
    },
    goodFor: {
        business: Tag.enum["goodFor:business"],
        couples: Tag.enum["goodFor:couples"],
        dates: Tag.enum["goodFor:dates"],
        families: Tag.enum["goodFor:families"],
        foodies: Tag.enum["goodFor:foodies"],
        friends: Tag.enum["goodFor:friends"],
        friendsCatchup: Tag.enum["goodFor:friends-catchup"],
        groups: Tag.enum["goodFor:groups"],
        locals: Tag.enum["goodFor:locals"],
        parties: Tag.enum["goodFor:parties"],
        privateDining: Tag.enum["goodFor:private-dining"],
        soloTrip: Tag.enum["goodFor:solo-trip"],
        tourists: Tag.enum["goodFor:tourists"],
        vegans: Tag.enum["goodFor:vegans"],
        vegetarians: Tag.enum["goodFor:vegetarians"],
    },
    cuisine: {
        african: Tag.enum["cuisine:african"],
        american: Tag.enum["cuisine:american"],
        asian: Tag.enum["cuisine:asian"],
        british: Tag.enum["cuisine:british"],
        chinese: Tag.enum["cuisine:chinese"],
        french: Tag.enum["cuisine:french"],
        greek: Tag.enum["cuisine:greek"],
        indian: Tag.enum["cuisine:indian"],
        italian: Tag.enum["cuisine:italian"],
        japanese: Tag.enum["cuisine:japanese"],
        latin: Tag.enum["cuisine:latin"],
        mediterranean: Tag.enum["cuisine:mediterranean"],
        mexican: Tag.enum["cuisine:mexican"],
        middleEastern: Tag.enum["cuisine:middle-eastern"],
        scandinavian: Tag.enum["cuisine:scandinavian"],
        seafood: Tag.enum["cuisine:seafood"],
        spanish: Tag.enum["cuisine:spanish"],
        steakhouse: Tag.enum["cuisine:steakhouse"],
    },
    activities: {
        adventurous: Tag.enum["activities:adventurous"],
        beach: Tag.enum["activities:beach"],
        cityBreak: Tag.enum["activities:city-break"],
        cultural: Tag.enum["activities:cultural"],
        hiking: Tag.enum["activities:hiking"],
        nature: Tag.enum["activities:nature"],
        sea: Tag.enum["activities:sea"],
        safari: Tag.enum["activities:safari"],
        skiing: Tag.enum["activities:skiing"],
    },
};

export const TagCategoryToDisplayName: Record<TagCategory, string> = {
    activities: "Activities",
    cuisine: "Cuisine",
    goodFor: "Good for",
    meals: "Meals",
    price: "Price range",
    type: "Type",
    vibe: "Vibe",
};

// deconstruct tags to allow to create category specific tag lists
const {
    price: { inexpensive, moderate, expensive, veryExpensive },
    type: {
        restaurant,
        cafe,
        popUp,
        bar,
        pub,
        wineBar,
        cocktailBar,
        rooftop,
        nightclub,
        liveMusic,
        commercialAirline,
        privateAirline,
        hotel,
        villa,
        apartment,
        house,
        resort,
        yacht,
        bedAndBreakfast,
        carRental,
        carTransfer,
        boatRental,
        charterBoat,
        helicopter,
        waterTaxi,
        sports,
        theatre,
        music,
        fashion,
        art,
        dance,
        museum,
        artGallery,
        historical,
        touristAttraction,
        mustDo,
        hiddenGem,
        outdoors,
        bookshop,
        watches,
        corporateGifts,
        personalGifts,
        collectibles,
        spa,
        sauna,
        yoga,
        ayurveda,
        reiki,
        massage,
        beautySalon,
        personalTrainer,
        retreat,
        surfing,
        wellnessHotel,
        workoutStudio,
        flowerDelivery,
        cleaningService,
        movingService,
        realEstate,
        education,
        childcare,
        bakery,
        foodCourt,
    },
    goodFor: {
        business,
        couples,
        dates,
        families,
        foodies,
        locals,
        groups,
        parties,
        privateDining,
        tourists,
        vegetarians,
        vegans,
        friends,
        friendsCatchup,
        soloTrip,
    },
    vibe: {
        boutique,
        casual,
        elegant,
        highEnd,
        localAndAuthentic,
        luxury,
        modern,
        quirky,
        romantic,
        touristy,
        traditional,
        trendy,
        fineDining,
        intimate,
    },
} = Tags;

const pricesExcludingFree = {
    veryExpensive,
    expensive,
    moderate,
    inexpensive,
};

export const diningTags: CategorySpecificTags = {
    type: {
        bakery,
        bar,
        cafe,
        foodCourt,
        popUp,
        restaurant,
    },
    goodFor: {
        business,
        dates,
        families,
        friendsCatchup,
        groups,
        privateDining,
        vegans,
        vegetarians,
    },
    vibe: {
        casual,
        elegant,
        fineDining,
        highEnd,
        intimate,
        localAndAuthentic,
        modern,
        quirky,
        romantic,
        traditional,
        trendy,
    },
    meals: { ...Tags.meals },
    cuisine: { ...Tags.cuisine },
    price: { ...pricesExcludingFree },
};

export const nightlifeTags: CategorySpecificTags = {
    type: {
        bar,
        pub,
        wineBar,
        cocktailBar,
        rooftop,
        nightclub,
        liveMusic,
    },
    goodFor: {
        business,
        dates,
        friendsCatchup,
        groups,
        parties,
    },
    vibe: {
        casual,
        elegant,
        highEnd,
        localAndAuthentic,
        modern,
        quirky,
        romantic,
        traditional,
        trendy,
    },
    price: { ...Tags.price },
};

export const flightTags: CategorySpecificTags = {
    type: {
        commercialAirline,
        privateAirline,
    },
};

export const accommodationTags: CategorySpecificTags = {
    type: {
        hotel,
        villa,
        apartment,
        house,
        resort,
        yacht,
        bedAndBreakfast,
    },
    goodFor: {
        business,
        couples,
        families,
        friends,
        groups,
        soloTrip,
    },
    vibe: {
        boutique,
        casual,
        elegant,
        localAndAuthentic,
        luxury,
        modern,
        quirky,
        romantic,
        trendy,
        traditional,
    },
    price: { ...Tags.price },
};

export const travelItineraryTags: CategorySpecificTags = {
    goodFor: {
        business,
        couples,
        families,
        friends,
        groups,
        soloTrip,
    },
    activities: { ...Tags.activities },
    price: { ...Tags.price },
};

export const transportationTags: CategorySpecificTags = {
    type: {
        carRental,
        carTransfer,
        boatRental,
        charterBoat,
        helicopter,
        waterTaxi,
    },
    price: { ...Tags.price },
};

export const eventTags: CategorySpecificTags = {
    type: {
        sports,
        theatre,
        music,
        fashion,
        art,
        dance,
    },
    price: { ...Tags.price },
};

export const localExperienceTags: CategorySpecificTags = {
    type: {
        museum,
        artGallery,
        historical,
        touristAttraction,
        mustDo,
        hiddenGem,
        outdoors,
        bookshop,
    },
    goodFor: {
        business,
        couples,
        families,
        foodies,
        friends,
        groups,
        locals,
        tourists,
    },
    vibe: {
        highEnd,
        localAndAuthentic,
        touristy,
    },
    price: { ...Tags.price },
};

export const shoppingTags: CategorySpecificTags = {
    type: {
        fashion,
        watches,
        corporateGifts,
        personalGifts,
        collectibles,
    },
    price: { ...Tags.price },
};

export const wellnessTags: CategorySpecificTags = {
    type: {
        spa,
        sauna,
        yoga,
        ayurveda,
        reiki,
        massage,
        beautySalon,
        personalTrainer,
        retreat,
        surfing,
        wellnessHotel,
        workoutStudio,
    },
    price: { ...Tags.price },
};

export const servicesTags: CategorySpecificTags = {
    type: {
        flowerDelivery,
        cleaningService,
        movingService,
        realEstate,
        education,
        childcare,
    },
    price: { ...Tags.price },
};
